import NextLink from 'next/link';
import { forwardRef } from 'react';

/**
 * Drop-in replacement for `next/link`.
 *
 * Next.js `<Link>` automatically prefetches `_next/data/...json` for every URL
 * in the viewport. The blog at /blog/* is served by a different stack, so those
 * prefetches 404 and waste bandwidth/server requests. For non-Next routes we
 * render a plain `<a>` tag instead so no Next.js data fetch is triggered.
 *
 * Also short-circuits for absolute URLs (http(s)://) which are always external.
 */
const NON_NEXT_PATH_PREFIXES = ['/blog/', '/blog'];

function isAbsoluteUrl(href) {
  return /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i.test(href);
}

function isNonNextRoute(href) {
  if (!href || typeof href !== 'string') return false;
  if (isAbsoluteUrl(href)) return true;
  return NON_NEXT_PATH_PREFIXES.some((prefix) => href === prefix || href.startsWith(prefix + '/') || href === '/blog');
}

const SmartLink = forwardRef(function SmartLink({ href, prefetch, replace, scroll, shallow, locale, ...rest }, ref) {
  // Strip Next-specific props from `rest` so they don't leak into <a> as invalid attributes.
  if (typeof href === 'string' && isNonNextRoute(href)) {
    return <a ref={ref} href={href} {...rest} />;
  }

  return (
    <NextLink
      ref={ref}
      href={href}
      prefetch={prefetch}
      replace={replace}
      scroll={scroll}
      shallow={shallow}
      locale={locale}
      {...rest}
    />
  );
});

export default SmartLink;
